'use client';

import { Container } from '@/components/container';
import { QuestionCard } from '@/components/question-card';
import { QuestionSkeleton } from '@/components/question-card-skeleton';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { useQuestion } from '@/hooks/useQuestion';
import { useSetupProfile } from '@/hooks/useSetupProfile';
import { cn, getRootLink } from '@/lib/utils';
import { useAuthStore } from '@/store';
import { Funnel, ListFilter, TrendingUp } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import RegisterDialog from './RegisterDialog';

export const SpotlightQuestionsBanner = () => {
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);

  const { getCategoriesApi } = useSetupProfile({
    loadCategories: true,
    filterType: 'TEASER',
  });

  const { getAllQuestions, getInfiniteQuestionsApi } = useQuestion({
    limit: 3,
    type: 'TEASER',
    categoryId: selectedCategory,
  });

  const router = useRouter();
  const { user } = useAuthStore();
  const questions = getAllQuestions();
  const categories = getCategoriesApi.data?.data['TEASER'] || [];
  const isLoading =
    getInfiniteQuestionsApi.isLoading || getCategoriesApi.isLoading;

  if (isLoading) {
    return (
      <div className="bg-ihs-accent-blue">
        <Container>
          <div className="py-8 flex flex-col gap-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="text-ihs-blue w-4 h-4" />
                <h2 className="font-bold text-xl">Spotlight Questions</h2>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <QuestionSkeleton />
              <QuestionSkeleton />
              <QuestionSkeleton />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (questions.length === 0) {
    return null;
  }

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value === 'all' ? [] : [value]);
  };

  const selectedCategoryName = selectedCategory[0]
    ? categories.find((category) => category.id === selectedCategory[0])?.name
    : 'Filter by Category';

  return (
    <div className="bg-ihs-accent-blue">
      <Container>
        <div className="py-8 flex flex-col gap-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-ihs-blue w-4 h-4" />
              <h2 className="font-bold text-xl">Spotlight Questions</h2>
            </div>

            <Select
              value={selectedCategory[0] || 'all'}
              onValueChange={handleCategoryChange}
            >
              <div className=" flex items-end flex-col">
                <SelectTrigger
                  className={cn(
                    buttonVariants({ variant: 'outline' }),
                    'bg-[#008080] text-white hover:bg-[#008080]/90 hover:text-white border-0'
                  )}
                  chevronClassName="text-white"
                >
                  <div className="flex items-center gap-2">
                    <Funnel className="h-4 w-4 text-white " />
                    <span>{selectedCategoryName}</span>
                  </div>
                </SelectTrigger>
                <div className="text-gray-700 flex flex-col items-end mt-4 text-sm">
                  <span>Looking for something specific? Use Search.</span>{' '}
                  {/* <br /> */}
                  <span>Browse by topic? Use Filters.</span>
                </div>
              </div>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className={cn('flex flex-col gap-4')}>
            {questions.map((question) => (
              <QuestionCard
                key={question.id}
                question={question}
                hideUserInfo
                hideReport
                slugRoute={getRootLink(question.questionType)}
              />
            ))}
          </div>

          <div className="flex md:flex-row flex-col items-center gap-2 justify-center">
            <Button
              variant="outline"
              onClick={() =>
                router.push(
                  `/spotlight-questions${selectedCategory[0] ? `?category=${selectedCategory[0]}` : ''}`
                )
              }
              className="border-ihs-dark-blue w-full md:w-auto text-ihs-dark-blue hover:text-ihs-dark-blue uppercase font-bold p-6"
            >
              See More Questions
            </Button>
            {!user ? (
              <RegisterDialog
                title="You've got something valuable to say."
                description="Create your free account to ask questions, contribute your experience, and help others navigate change."
                redirectUrl="/ask-question"
              >
                <Button
                  variant="outline"
                  className="border-ihs-dark-blue w-full md:w-auto text-ihs-dark-blue hover:text-ihs-dark-blue uppercase font-bold p-6"
                >
                  Add question
                </Button>
              </RegisterDialog>
            ) : (
              <Button
                variant="outline"
                onClick={() => router.push('ask-question')}
                className="border-ihs-dark-blue w-full md:w-auto text-ihs-dark-blue hover:text-ihs-dark-blue uppercase font-bold p-6"
              >
                Add question
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => router.push('idea-starters')}
              className="border-ihs-dark-blue w-full md:w-auto text-ihs-dark-blue hover:text-ihs-dark-blue uppercase font-bold p-6"
            >
              Idea Starters
            </Button>
            {!user && (
              <Button
                variant="default"
                onClick={() => router.push('sign-up')}
                className="bg-ihs-dark-blue w-full md:w-auto uppercase hover:bg-ihs-dark-blue/90 font-bold p-6"
              >
                Register now
              </Button>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};
